/**
 * PMInterface - Main PM Command Center Interface
 *
 * 5-Tab Lifecycle Structure (per Integration Strategy):
 * 1. Pipeline - What needs a PM? (intake only, unassigned projects)
 * 2. Project Review - What do we have? (IN_INTAKE, preparation phase)
 * 3. WO Review - Is this what production sees? (WO_DRAFT, review & publish)
 * 4. Active Projects - What's happening now? (WO_PUBLISHED, in production)
 * 5. Completed - What have I finished? (READY_FOR_BILLING, CLOSED)
 *
 * Lifecycle Invariants:
 * - A project exists in exactly ONE lifecycle tab at a time
 * - Tab membership is derived exclusively from pm_readiness
 * - UI routing may not override lifecycle state
 *
 * Notifications are NOT a tab - they appear as a global bell icon/panel.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import PMPipelineV2 from "./PMPipelineV2";
import PMMyProjects from "./PMMyProjects";
import PMNotificationCenter from "./PMNotificationCenter";
import PMWorkOrderReviewTab from "./PMWorkOrderReviewTab";
import PMActiveProjects from "./PMActiveProjects";
import PMCompletedProjects from "./PMCompletedProjects";
import PMPublishStateIndicator from "./PMPublishStateIndicator";

const apiScopes = [
  "api://2350508b-6d67-408a-9c55-c5040357d90d/b351e950-d526-4ee4-b95b-03f6ec89cd3d/access_as_user",
];

// 5-Tab Lifecycle Structure
// Tab membership is derived exclusively from pm_readiness values
const TABS = [
  {
    id: "pipeline",
    label: "Pipeline",
    icon: "📊",
    description: "What needs a PM? Unassigned projects awaiting intake.",
    // pm_readiness: NULL or NEEDS_PM (unassigned only)
  },
  {
    id: "project-review",
    label: "Project Review",
    icon: "📋",
    description: "What do we have? Preparation & SKU tracking before WO.",
    // pm_readiness: IN_INTAKE
  },
  {
    id: "wo-review",
    label: "WO Review",
    icon: "📄",
    description: "Is this what production sees? Review & publish work orders.",
    // pm_readiness: WO_DRAFT
  },
  {
    id: "active",
    label: "Active Projects",
    icon: "🔄",
    description: "What's happening now? Projects in production.",
    // pm_readiness: WO_PUBLISHED
  },
  {
    id: "completed",
    label: "Completed",
    icon: "✓",
    description: "What have I finished? Historical record.",
    // pm_readiness: READY_FOR_BILLING or CLOSED
  },
];

export default function PMInterface({ onBackToApp }) {
  // MSAL Auth
  const { instance, accounts } = useMsal();

  // Acquire access token
  const acquireAccessToken = useCallback(async () => {
    if (accounts.length === 0) return null;
    try {
      const response = await instance.acquireTokenSilent({
        scopes: apiScopes,
        account: accounts[0],
      });
      return response.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        const response = await instance.acquireTokenPopup({ scopes: apiScopes });
        return response.accessToken;
      }
      throw err;
    }
  }, [instance, accounts]);

  // Tab state - Pipeline is default
  const [activeTab, setActiveTab] = useState("pipeline");

  // Ref for pipeline view to trigger refresh
  const pipelineRef = useRef(null);

  // Selected items for detail views
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  // Notification badge count
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  // Load unread notification count
  const loadUnreadCount = useCallback(async () => {
    try {
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      const res = await fetch("/api/pm-center/notifications", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (res.ok) {
        const data = await res.json();
        setUnreadNotifications(data.unreadCount || 0);
      }
    } catch (err) {
      console.error("Error loading notification count:", err);
    }
  }, [acquireAccessToken]);

  // Load notification count on mount and periodically
  useEffect(() => {
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [loadUnreadCount]);

  // Handle work order generation from pipeline
  const handleGenerateWorkOrder = useCallback(async (project) => {
    try {
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      // Check if we have an order number
      if (!project.order_number) {
        // For pending quotes, we can't generate yet
        alert("Cannot generate work order without an order number. Please wait for the order to be created in JobBoss.");
        return;
      }

      // Generate work order
      const res = await fetch("/api/pm-center/work-orders/generate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          orderNumber: project.order_number,
          quoteNumber: project.quote_number,
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to generate work order");
      }

      const result = await res.json();
      alert(`Work Order generated: ${result.fileName}\n\nSharePoint URL copied to clipboard.`);

      // Copy SharePoint URL to clipboard
      if (result.sharepointUrl) {
        await navigator.clipboard.writeText(result.sharepointUrl);
      }

      // Refresh pipeline to show updated state
      pipelineRef.current?.refresh();
    } catch (err) {
      console.error("Error generating work order:", err);
      alert(`Error: ${err.message}`);
    }
  }, [acquireAccessToken]);

  // Handle viewing work order details
  const handleViewWorkOrder = useCallback((project) => {
    setSelectedWorkOrder(project);
    setActiveTab("workorders");
  }, []);

  // Handle "Assign to Me" action
  const handleAssignToMe = useCallback(async (project) => {
    try {
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      const res = await fetch(`/api/pm-center/projects/${project.quote_number}/assign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          // Backend will extract PM info from the token
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        if (error.error === "Project already assigned") {
          alert(`This project is already assigned to ${error.assignedTo || 'another PM'}.`);
        } else {
          throw new Error(error.error || "Failed to assign project");
        }
        return;
      }

      const result = await res.json();
      alert(`Project ${project.quote_number} assigned to you!\n\nYou can now generate the work order.`);

      // Refresh pipeline to show updated assignment
      pipelineRef.current?.refresh();
    } catch (err) {
      console.error("Error assigning project:", err);
      alert(`Error: ${err.message}`);
    }
  }, [acquireAccessToken]);

  // Handle "PDF Ready" action
  const handleMarkPDFReady = useCallback(async (project) => {
    try {
      const accessToken = await acquireAccessToken();
      if (!accessToken) return;

      // Confirm action
      const confirmed = confirm(
        `Mark PDF as ready for customer approval?\n\n` +
        `Project: ${project.quote_number} - ${project.customer_name}\n` +
        `This will notify the sales rep.`
      );
      if (!confirmed) return;

      const res = await fetch(`/api/pm-center/projects/${project.quote_number}/pdf-ready`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          notes: "", // Could add a notes field in the future
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to mark PDF ready");
      }

      const result = await res.json();

      if (result.alreadyMarked) {
        alert(`PDF was already marked ready within the last 24 hours.\n\nLast marked: ${new Date(result.lastMarkedAt).toLocaleString()}`);
      } else {
        alert(`PDF marked as ready!\n\nSales rep has been notified.`);
      }
    } catch (err) {
      console.error("Error marking PDF ready:", err);
      alert(`Error: ${err.message}`);
    }
  }, [acquireAccessToken]);

  // Handle selecting a project for details
  const handleSelectProject = useCallback((project) => {
    setSelectedProject(project);
  }, []);

  // Get current user email for PM identification
  const currentUserEmail = useMemo(() => {
    return accounts[0]?.username || accounts[0]?.email || "";
  }, [accounts]);

  // Notification panel state (slide-out, not a tab)
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);

  // Render tab content based on lifecycle stage
  const renderTabContent = () => {
    switch (activeTab) {
      case "pipeline":
        // Tab 1: Pipeline - Unassigned projects awaiting PM
        // Filter: assigned_pm_id IS NULL
        return (
          <PMPipelineV2
            acquireAccessToken={acquireAccessToken}
          />
        );

      case "project-review":
        // Tab 2: Project Review - Projects in intake/preparation
        // Filter: pm_readiness = 'IN_INTAKE'
        return (
          <PMMyProjects
            acquireAccessToken={acquireAccessToken}
            lifecycleFilter="IN_INTAKE"
          />
        );

      case "wo-review":
        // Tab 3: WO Review - Work orders ready for review/publish
        // Filter: pm_readiness = 'WO_DRAFT'
        return (
          <PMWorkOrderReviewTab
            acquireAccessToken={acquireAccessToken}
            lifecycleFilter="WO_DRAFT"
          />
        );

      case "active":
        // Tab 4: Active Projects - In production
        // Filter: pm_readiness = 'WO_PUBLISHED'
        return (
          <PMActiveProjects
            acquireAccessToken={acquireAccessToken}
            lifecycleFilter="WO_PUBLISHED"
          />
        );

      case "completed":
        // Tab 5: Completed - Historical record
        // Filter: pm_readiness IN ('READY_FOR_BILLING', 'CLOSED')
        return (
          <PMCompletedProjects acquireAccessToken={acquireAccessToken} />
        );

      default:
        return null;
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBackToApp}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to app"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-gray-900">PM Command Center</h1>
          </div>

          {/* Right side: Notifications bell + User indicator */}
          <div className="flex items-center gap-4">
            {/* Notification Bell */}
            <button
              onClick={() => setShowNotificationPanel(!showNotificationPanel)}
              className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Notifications"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              {/* Badge */}
              {unreadNotifications > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {unreadNotifications > 9 ? "9+" : unreadNotifications}
                </span>
              )}
            </button>

            {/* Current user indicator */}
            <div className="text-sm text-gray-500">
              {currentUserEmail && (
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  {currentUserEmail.split("@")[0]}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 5-Tab Lifecycle Navigation with Workflow Arrows */}
        <div className="flex items-center mt-3">
          {TABS.map((tab, index) => (
            <div key={tab.id} className="flex items-center">
              <button
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg transition-colors
                  ${
                    activeTab === tab.id
                      ? "bg-purple-100 text-purple-700 font-medium"
                      : "text-gray-700 hover:bg-gray-100"
                  }
                `}
                title={tab.description}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
              {/* Workflow arrow between tabs */}
              {index < TABS.length - 1 && (
                <svg
                  className="w-5 h-5 text-gray-300 mx-1 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {renderTabContent()}
      </div>

      {/* Work Order State Indicator (when work order selected) */}
      {selectedWorkOrder && activeTab !== "wo-review" && (
        <div className="fixed bottom-4 right-4">
          <PMPublishStateIndicator
            workOrder={selectedWorkOrder}
            onClick={() => setActiveTab("wo-review")}
          />
        </div>
      )}

      {/* Notification Slide-Out Panel */}
      {showNotificationPanel && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-40"
            onClick={() => setShowNotificationPanel(false)}
          />
          {/* Panel */}
          <div className="fixed top-0 right-0 h-full w-96 bg-white shadow-xl z-50 flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
              <button
                onClick={() => setShowNotificationPanel(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto">
              <PMNotificationCenter
                acquireAccessToken={acquireAccessToken}
                onUnreadCountChange={setUnreadNotifications}
                compact={true}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
